import cn from "classnames";
import { Formik } from "formik";
import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import TextField from "@mui/material/TextField";
import { Switch, Typography, FormControlLabel } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

import { BaseButton } from "@/src/component/base/BaseButton";
import { DialogWithExit } from "@/src/component/base/DialogWithExit";
import { useToast } from "@/src/hook/provider";
import { ProviderHooks } from "@/src/hook";
import { TrackingLogic } from "@/src/logic";
import { contactUsValidationSchema } from "@/src/logic/ContactUsLogic";
import { FrontendContactService, FrontendTrackingService } from "@/src/service";
import { StrapiComponentType } from "@/src/type";
import type { ContactUsFormData } from "@/src/type/contact";
import { FormHelper } from "@/src/util";

import { FormStyles } from "@/styles/util";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./ContactUsFormMri.module.scss";

interface ContactUsFormMRIProps {
  loading: boolean;
  setLoading: (val: boolean) => void;
  isDark?: boolean;
  contactUs: StrapiComponentType.Section.ContactUs;
}

function ContactUsFormMRI({ loading, setLoading, isDark, contactUs }: ContactUsFormMRIProps) {
  const [registerPopup, showRegisterPopup] = useState<boolean>(false);
  const toast = useToast();
  const cms = ProviderHooks.useCMS();
  const initialValues: ContactUsFormData = {
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "",
    phoneNumber: "",
    isBroker: false,
    agreeToSMS: false,
    brokerageName: "",
  };

  const handleRegisterPopup = () => {
    showRegisterPopup((registerPopup) => !registerPopup);
  };

  function getHref(href: string, isExternal: boolean): string {
    return isExternal ? href : cms.constructLink(href);
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={contactUsValidationSchema(cms?.webConfig?.formValidation)}
        onSubmit={async (values) => {
          setLoading(true);
          const trackingParams = TrackingLogic.getUTMParams();
          const leadBody = {
            form: values,
            fub: {
              campaign: {
                source: trackingParams.utmSource,
                campaign: trackingParams.utmCampaign,
                content: trackingParams.utmContent,
                term: trackingParams.utmTerm,
                medium: trackingParams.utmMedium,
              },
              pageTitle: document.title,
              pageUrl: window.location.href,
              pageReferrer: trackingParams.referrer,
            },
          };
          const postContactForm = await FrontendContactService.postContactForm(leadBody);
          if (postContactForm.success) {
            const leadId = postContactForm?.response?.strapiLeadResponse?.response?.data?.id;
            FrontendTrackingService.postTrackingLog({
              action: "click",
              actionId: leadId ? `${leadId}` : "contactUsSubmitted",
              actionIdType: "strapi.lead.id",
              actionMessage: "contactUsSubmitted",
              triggeredFrom: "ContactUsFormMRI.tsx",
            });

            toast.addMessage({
              title: "Contact Us Message Success",
              subtitle: "Your message was successfully sent.",
              type: "success",
            });
          } else {
            toast.addMessage({
              title: "Contact Us Message Failed",
              subtitle: "Sorry, your message could not be sent at this time.",
              type: "error",
            });
          }
          setLoading(false);
        }}
      >
        {(formik) => (
          <form
            onSubmit={formik.handleSubmit}
            className={styles.form}
            suppressHydrationWarning={true}
          >
            <div className={styles.formSplit}>
              <TextField
                name="firstName"
                label={contactUs.formFirstNameLabel}
                aria-label={contactUs.formFirstNameLabel}
                placeholder={contactUs.formFirstNameLabel}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Image
                        src="/image/icon_profile.svg"
                        width={24}
                        height={24}
                        alt="First Name Icon"
                      />
                    </InputAdornment>
                  ),
                }}
                helperText={FormHelper.formikErrorMessage<any>(formik, "firstName")}
                error={FormHelper.formikCheckError<any>(formik, "firstName")}
                inputProps={{
                  maxLength: 50,
                }}
                sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.firstName, !isDark) }}
              />
              <TextField
                name="lastName"
                label={contactUs.formLastNameLabel}
                aria-label={contactUs.formLastNameLabel}
                placeholder={contactUs.formLastNameLabel}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Image
                        src="/image/icon_profile.svg"
                        width={24}
                        height={24}
                        alt="Last Name Icon"
                      />
                    </InputAdornment>
                  ),
                }}
                helperText={FormHelper.formikErrorMessage<any>(formik, "lastName")}
                error={FormHelper.formikCheckError<any>(formik, "lastName")}
                inputProps={{
                  maxLength: 50,
                }}
                sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.lastName, !isDark) }}
              />
            </div>
            <TextField
              name="email"
              label={contactUs.formEmailLabel}
              aria-label={contactUs.formEmailLabel}
              placeholder={contactUs.formEmailLabel}
              value={formik.values.email}
              onChange={formik.handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Image
                      src="/image/icon_email.svg"
                      width={24}
                      height={24}
                      alt="Email Icon"
                    />
                  </InputAdornment>
                ),
              }}
              fullWidth
              helperText={FormHelper.formikErrorMessage<any>(formik, "email")}
              error={FormHelper.formikCheckError<any>(formik, "email")}
              inputProps={{
                maxLength: 50,
              }}
              sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.email, !isDark) }}
            />
            <div className={styles.formSplitPhone}>
              <TextField
                name="countryCode"
                value={FormHelper.displayCountryCode(formik.values.countryCode ?? "")}
                onChange={(e) => {
                  FormHelper.handleCountryCode(e, formik.setFieldValue);
                }}
                label={contactUs.formCountryCodeLabel}
                aria-label={contactUs.formCountryCodeLabel}
                placeholder={contactUs.formCountryCodeLabel}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Image
                        src="/image/icon_world.svg"
                        width={24}
                        height={24}
                        alt="World Icon"
                      />
                    </InputAdornment>
                  ),
                }}
                helperText={FormHelper.formikErrorMessage<any>(formik, "countryCode")}
                error={FormHelper.formikCheckError<any>(formik, "countryCode")}
                inputProps={{
                  maxLength: 4,
                  inputMode: "tel",
                }}
                sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.countryCode, !isDark) }}
              />
              <TextField
                name="phoneNumber"
                label={contactUs.formPhoneNumberLabel}
                aria-label={contactUs.formPhoneNumberLabel}
                placeholder={contactUs.formPhoneNumberLabel}
                value={FormHelper.displayPhoneNumber(formik.values.phoneNumber ?? "")}
                onChange={(e) => {
                  FormHelper.handlePhoneNumber(e, formik.setFieldValue);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Image
                        src="/image/icon_phone_dark.svg"
                        width={24}
                        height={24}
                        alt="Phone Icon"
                      />
                    </InputAdornment>
                  ),
                }}
                helperText={FormHelper.formikErrorMessage<any>(formik, "phoneNumber")}
                error={FormHelper.formikCheckError<any>(formik, "phoneNumber")}
                inputProps={{
                  maxLength: 12,
                  inputMode: "tel",
                }}
                sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.phoneNumber, !isDark) }}
              />
            </div>
            <div className={styles.brokerCheckboxContainer}>
              <FormControlLabel
                control={
                  <Switch
                    name="isBroker"
                    checked={formik.values.isBroker}
                    onChange={() => {
                      formik.setFieldValue("isBroker", !formik.values.isBroker);
                      if (!formik.values.isBroker) {
                        handleRegisterPopup();
                        FrontendTrackingService.postTrackingLog({
                          action: "click",
                          actionId: "isBroker",
                          actionIdType: "field",
                          actionMessage: "clickIsBroker",
                          triggeredFrom: "ContactUsFormMRI.tsx",
                        });
                      }
                    }}
                    sx={FormStyles.checkboxStyle(isDark)}
                  />
                }
                label={
                  <Typography
                    className={cn(styles.brokerCheckboxLabel)}
                    color={isDark ? theme.primaryMain : theme.secondaryMain}
                  >
                    {contactUs.formBrokerCheckboxLabel}
                  </Typography>
                }
              />
            </div>
            {formik.values.isBroker && (
              <TextField
                name="brokerageName"
                label={isDark ? contactUs.formBrokerageNameLabel : undefined}
                aria-label={contactUs.formBrokerageNameLabel}
                placeholder={contactUs.formBrokerageNameLabel}
                value={formik.values.brokerageName}
                onChange={formik.handleChange}
                fullWidth
                helperText={FormHelper.formikErrorMessage<any>(formik, "brokerageName")}
                error={FormHelper.formikCheckError<any>(formik, "brokerageName")}
                inputProps={{
                  maxLength: 75,
                }}
                sx={{ ...FormStyles.themeStyle(isDark) }}
              />
            )}
            <div className={styles.brokerCheckboxContainer}>
              <FormControlLabel
                control={
                  <Switch
                    name="agreeToSMS"
                    checked={formik.values.agreeToSMS}
                    onChange={() => {
                      formik.setFieldValue("agreeToSMS", !formik.values.agreeToSMS);
                      if (!formik.values.agreeToSMS) {
                        FrontendTrackingService.postTrackingLog({
                          action: "click",
                          actionId: "agreeToSMS",
                          actionIdType: "field",
                          actionMessage: "clickAgreeToSMS",
                          triggeredFrom: "ContactUsFormMRI.tsx",
                        });
                      }
                    }}
                    sx={FormStyles.checkboxStyle(isDark)}
                  />
                }
                label={
                  <div className={styles.disclaimerCheckboxLabel}>
                    <Typography
                      className={cn(styles.brokerCheckboxLabel)}
                      color={isDark ? theme.primaryMain : theme.secondaryMain}
                    >
                      {contactUs?.submissionDisclaimer}
                      {(contactUs?.submissionDisclaimerLinks ?? []).map((link, i) => (
                        <span key={i}>
                          {i > 0 ? " & " : " "}
                          <Link
                            key={i}
                            href={getHref(link.href, link.isExternal)}
                            target={link.target ?? "_blank"}
                          >
                            {link.label}
                          </Link>
                        </span>
                      ))}
                    </Typography>
                  </div>
                }
              />
            </div>
            <div className={styles.formBtn}>
              <BaseButton
                type="submit"
                variant="outlined"
                loading={loading}
                color={isDark ? "primary" : "secondary"}
                size="large"
                sx={{
                  "&:disabled": {
                    borderColor: `${isDark ? theme.primaryMain : theme.secondaryMain} !important`,
                    color: `${isDark ? theme.primaryMain : theme.secondaryMain} !important`,
                  },
                }}
              >
                {contactUs.formSubmitButtonLabel}
              </BaseButton>
            </div>
            <div
              className={styles.disclaimer}
              style={{ display: "none" }}
            >
              <Typography
                color={isDark ? theme.primaryMain : theme.secondaryMain}
                variant="tiny"
              >
                {contactUs?.submissionDisclaimer}
              </Typography>
            </div>
          </form>
        )}
      </Formik>
      <DialogWithExit
        dialogProps={{
          open: registerPopup,
          onClose: handleRegisterPopup,
        }}
        onExit={handleRegisterPopup}
      >
        <div className={styles.registerPopup}>
          {contactUs.registerPopupTitle && <Typography variant="h4">{contactUs.registerPopupTitle}</Typography>}
          {contactUs.registerPopupMessage && <Typography>{contactUs.registerPopupMessage}</Typography>}
          <Link href={cms.constructLink("/broker/register/identity")}>
            <BaseButton
              type="button"
              variant="contained"
              color="primary"
              size="medium"
              fullWidth
            >
              {contactUs.registerPopupButtonText}
            </BaseButton>
          </Link>
        </div>
      </DialogWithExit>
    </>
  );
}

export default ContactUsFormMRI;
